import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { cn } from '@/utils/cn'
import {
  buildBreakdownGroups,
  CURRENCY_FIELDS,
  METRIC_FIELDS,
  METRIC_LABELS,
  PERCENT_FIELDS,
  PRICE_FIELDS,
  UNIT_FIELDS,
  type ClassGroup,
  type DeptGroup,
  type GroupKey,
  type Metrics,
  type MetricGroups,
} from './financialsBreakdownData'
import type { FinancialsFilterState } from './financialsMockData'

interface Props {
  filters: FinancialsFilterState
}

const STICKY_COLS = [
  { key: 'calendar', label: 'Calendar', defaultWidth: 200 },
  { key: 'season', label: 'Season', defaultWidth: 110 },
  { key: 'department', label: 'Department', defaultWidth: 220 },
  { key: 'className', label: 'Class', defaultWidth: 200 },
  { key: 'subclass', label: 'Subclass', defaultWidth: 200 },
] as const

const MIN_COL_WIDTH = 60

function computeLefts(widths: number[]): number[] {
  const lefts: number[] = []
  for (let i = 0; i < widths.length; i++) {
    lefts.push(i === 0 ? 0 : lefts[i - 1] + widths[i - 1])
  }
  return lefts
}

interface StickyContextValue {
  widths: number[]
  lefts: number[]
}

const StickyContext = createContext<StickyContextValue | null>(null)

function useSticky(): StickyContextValue {
  const ctx = useContext(StickyContext)
  if (!ctx) throw new Error('StickyContext missing')
  return ctx
}

const GROUP_DEFS: Array<{ key: GroupKey; label: string; headBg: string }> = [
  { key: 'planned', label: 'PLANNED', headBg: 'bg-slate-100' },
  { key: 'assortment', label: 'ASSORTMENT', headBg: 'bg-sky-100' },
  { key: 'varDollar', label: '$ VAR TO PLAN', headBg: 'bg-amber-100' },
  { key: 'varPercent', label: '% VAR TO PLAN', headBg: 'bg-emerald-100' },
]

const METRIC_COL_WIDTH = 110

function formatMetric(
  field: keyof Metrics,
  value: number,
  isVarPercent: boolean
): string {
  if (!Number.isFinite(value)) return '—'
  const isPercent = PERCENT_FIELDS.has(field) || isVarPercent
  const isCurrency = CURRENCY_FIELDS.has(field)
  const isPrice = PRICE_FIELDS.has(field)
  const isUnit = UNIT_FIELDS.has(field)

  if (isVarPercent || isPercent) {
    return `${value.toFixed(1)}%`
  }
  if (isCurrency) {
    return value.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    })
  }
  if (isPrice) {
    return value.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  }
  if (isUnit) {
    return Math.round(value).toLocaleString('en-US')
  }
  return value.toLocaleString('en-US')
}

function Chevron({ expanded }: { expanded: boolean }) {
  return expanded ? (
    <ChevronDown className="inline h-3 w-3 shrink-0" />
  ) : (
    <ChevronRight className="inline h-3 w-3 shrink-0" />
  )
}

function StickyCell({
  index,
  bgClass,
  className,
  zIndex = 1,
  children,
  isHeader = false,
  topClass,
  showResizer = false,
  onResizeStart,
}: {
  index: number
  bgClass: string
  className?: string
  zIndex?: number
  children?: React.ReactNode
  isHeader?: boolean
  topClass?: string
  showResizer?: boolean
  onResizeStart?: (e: React.MouseEvent) => void
}) {
  const { widths, lefts } = useSticky()
  const Tag = isHeader ? 'th' : 'td'
  const width = widths[index]
  return (
    <Tag
      style={{
        left: lefts[index],
        width,
        minWidth: width,
        maxWidth: width,
        zIndex,
      }}
      className={cn(
        'sticky px-3 py-2 text-left whitespace-nowrap overflow-hidden text-ellipsis',
        bgClass,
        topClass,
        className
      )}
    >
      {children}
      {showResizer && (
        <span
          onMouseDown={onResizeStart}
          onClick={e => e.stopPropagation()}
          className="absolute top-0 right-0 z-50 flex h-full w-2 cursor-col-resize items-center justify-center select-none hover:bg-primary/20"
          title="Drag to resize"
        >
          <span className="block h-4 w-px bg-border" />
        </span>
      )}
    </Tag>
  )
}

function MetricRow({
  metrics,
  bold,
  textCls,
}: {
  metrics: MetricGroups
  bold?: boolean
  textCls?: string
}) {
  return (
    <>
      {GROUP_DEFS.map(group =>
        METRIC_FIELDS.map(field => {
          const value = metrics[group.key][field]
          const isVarPercent = group.key === 'varPercent'
          let signClass = ''
          if (
            (group.key === 'varDollar' || group.key === 'varPercent') &&
            Number.isFinite(value)
          ) {
            if (value > 0) signClass = 'text-emerald-700'
            else if (value < 0) signClass = 'text-rose-700'
          }
          return (
            <td
              key={`${group.key}-${field}`}
              style={{
                width: METRIC_COL_WIDTH,
                minWidth: METRIC_COL_WIDTH,
              }}
              className={cn(
                'px-3 py-2 text-right tabular-nums whitespace-nowrap',
                bold && 'font-semibold',
                textCls,
                signClass
              )}
            >
              {formatMetric(field, value, isVarPercent)}
            </td>
          )
        })
      )}
    </>
  )
}

export function FinancialsBreakdownTable({ filters }: Props) {
  const groups = useMemo(() => buildBreakdownGroups(filters), [filters])
  const [collapsedDepts, setCollapsedDepts] = useState<Set<string>>(new Set())
  const [collapsedClasses, setCollapsedClasses] = useState<Set<string>>(
    new Set()
  )
  const [widths, setWidths] = useState<number[]>(() =>
    STICKY_COLS.map(c => c.defaultWidth)
  )
  const lefts = useMemo(() => computeLefts(widths), [widths])
  const stickyValue = useMemo(() => ({ widths, lefts }), [widths, lefts])

  const dragRef = useRef<{
    index: number
    startX: number
    startWidth: number
  } | null>(null)

  const handleResizeStart = useCallback(
    (index: number) => (e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      dragRef.current = {
        index,
        startX: e.clientX,
        startWidth: widths[index],
      }
      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'
    },
    [widths]
  )

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      const drag = dragRef.current
      if (!drag) return
      const delta = e.clientX - drag.startX
      const next = Math.max(MIN_COL_WIDTH, drag.startWidth + delta)
      setWidths(prev => {
        if (prev[drag.index] === next) return prev
        const out = [...prev]
        out[drag.index] = next
        return out
      })
    }
    const onUp = () => {
      if (!dragRef.current) return
      dragRef.current = null
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
    return () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
    }
  }, [])

  const toggleDept = (key: string) => {
    setCollapsedDepts(prev => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const toggleClass = (key: string) => {
    setCollapsedClasses(prev => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const metricColsPerGroup = METRIC_FIELDS.length

  return (
    <StickyContext.Provider value={stickyValue}>
      <div className="overflow-hidden rounded-lg border border-border bg-white shadow-sm">
        <div className="max-h-[640px] overflow-auto">
          <table
            className="w-full border-separate text-xs"
            style={{ borderSpacing: 0 }}
          >
            <thead>
              {/* Top header row: group labels */}
              <tr>
                {STICKY_COLS.map((col, idx) => (
                  <StickyCell
                    key={col.key}
                    index={idx}
                    isHeader
                    bgClass="bg-white"
                    topClass="top-0"
                    className="border-b border-r border-border h-10 font-semibold text-foreground"
                    zIndex={30}
                  />
                ))}
                {GROUP_DEFS.map(group => (
                  <th
                    key={group.key}
                    colSpan={metricColsPerGroup}
                    className={cn(
                      'sticky top-0 z-20 border-b border-r border-border px-3 py-2 text-center text-xs font-semibold uppercase tracking-wide text-foreground',
                      group.headBg
                    )}
                  >
                    {group.label}
                  </th>
                ))}
              </tr>
              {/* Second header row: dim labels + metric labels */}
              <tr>
                {STICKY_COLS.map((col, idx) => (
                  <StickyCell
                    key={col.key}
                    index={idx}
                    isHeader
                    bgClass="bg-white"
                    topClass="top-10"
                    className="border-b border-r border-border h-10 font-semibold text-foreground"
                    zIndex={30}
                    showResizer
                    onResizeStart={handleResizeStart(idx)}
                  >
                    {col.label}
                  </StickyCell>
                ))}
                {GROUP_DEFS.map(group =>
                  METRIC_FIELDS.map((field, i) => (
                    <th
                      key={`${group.key}-${field}`}
                      style={{
                        width: METRIC_COL_WIDTH,
                        minWidth: METRIC_COL_WIDTH,
                      }}
                      className={cn(
                        'sticky top-10 z-10 border-b border-border px-3 py-2 text-right text-xs font-semibold text-foreground',
                        group.headBg,
                        i === metricColsPerGroup - 1 && 'border-r border-border'
                      )}
                    >
                      {METRIC_LABELS[field]}
                    </th>
                  ))
                )}
              </tr>
            </thead>
            <tbody>
              {groups.map(dept => (
                <DeptBlock
                  key={dept.deptKey}
                  dept={dept}
                  deptCollapsed={collapsedDepts.has(dept.deptKey)}
                  collapsedClasses={collapsedClasses}
                  onToggleDept={() => toggleDept(dept.deptKey)}
                  onToggleClass={toggleClass}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </StickyContext.Provider>
  )
}

function DeptBlock({
  dept,
  deptCollapsed,
  collapsedClasses,
  onToggleDept,
  onToggleClass,
}: {
  dept: DeptGroup
  deptCollapsed: boolean
  collapsedClasses: Set<string>
  onToggleDept: () => void
  onToggleClass: (key: string) => void
}) {
  const rowBg = 'bg-slate-50'
  return (
    <>
      <tr
        className={cn(
          'cursor-pointer font-semibold hover:brightness-95',
          rowBg
        )}
        onClick={onToggleDept}
      >
        <StickyCell index={0} bgClass={rowBg} className="font-medium">
          {dept.calendar}
        </StickyCell>
        <StickyCell index={1} bgClass={rowBg}>
          {dept.season}
        </StickyCell>
        <StickyCell index={2} bgClass={rowBg}>
          <span className="inline-flex items-center gap-1">
            <Chevron expanded={!deptCollapsed} />
            {dept.department}
          </span>
        </StickyCell>
        <StickyCell index={3} bgClass={rowBg} />
        <StickyCell
          index={4}
          bgClass={rowBg}
          className="border-r border-border"
        />
        <MetricRow metrics={dept.metrics} bold />
      </tr>

      {!deptCollapsed &&
        dept.classes.map(cls => (
          <ClassBlock
            key={cls.classKey}
            dept={dept}
            cls={cls}
            classCollapsed={collapsedClasses.has(cls.classKey)}
            onToggleClass={() => onToggleClass(cls.classKey)}
          />
        ))}

      {!deptCollapsed && (
        <tr className={cn('font-semibold', 'bg-slate-100')}>
          <StickyCell index={0} bgClass="bg-slate-100" />
          <StickyCell index={1} bgClass="bg-slate-100" />
          <StickyCell index={2} bgClass="bg-slate-100">
            {dept.department}
          </StickyCell>
          <StickyCell index={3} bgClass="bg-slate-100">
            Dept Total
          </StickyCell>
          <StickyCell
            index={4}
            bgClass="bg-slate-100"
            className="border-r border-border"
          />
          <MetricRow metrics={dept.metrics} bold />
        </tr>
      )}
    </>
  )
}

function ClassBlock({
  dept,
  cls,
  classCollapsed,
  onToggleClass,
}: {
  dept: DeptGroup
  cls: ClassGroup
  classCollapsed: boolean
  onToggleClass: () => void
}) {
  return (
    <>
      <tr
        className="cursor-pointer bg-white hover:bg-muted/30"
        onClick={onToggleClass}
      >
        <StickyCell
          index={0}
          bgClass="bg-white"
          className="text-muted-foreground"
        >
          {dept.calendar}
        </StickyCell>
        <StickyCell
          index={1}
          bgClass="bg-white"
          className="text-muted-foreground"
        >
          {dept.season}
        </StickyCell>
        <StickyCell
          index={2}
          bgClass="bg-white"
          className="text-muted-foreground"
        >
          {dept.department}
        </StickyCell>
        <StickyCell index={3} bgClass="bg-white">
          <span className="inline-flex items-center gap-1 font-medium">
            <Chevron expanded={!classCollapsed} />
            {cls.className}
          </span>
        </StickyCell>
        <StickyCell
          index={4}
          bgClass="bg-white"
          className="border-r border-border"
        />
        <MetricRow metrics={cls.metrics} bold />
      </tr>

      {!classCollapsed &&
        cls.rows.map((row, i) => (
          <tr
            key={`${cls.classKey}-${i}`}
            className="bg-white hover:bg-muted/20"
          >
            <StickyCell
              index={0}
              bgClass="bg-white"
              className="text-muted-foreground"
            >
              {dept.calendar}
            </StickyCell>
            <StickyCell
              index={1}
              bgClass="bg-white"
              className="text-muted-foreground"
            >
              {dept.season}
            </StickyCell>
            <StickyCell
              index={2}
              bgClass="bg-white"
              className="text-muted-foreground"
            >
              {dept.department}
            </StickyCell>
            <StickyCell
              index={3}
              bgClass="bg-white"
              className="text-muted-foreground"
            >
              {cls.className}
            </StickyCell>
            <StickyCell
              index={4}
              bgClass="bg-white"
              className="border-r border-border text-foreground"
            >
              {row.subclass}
            </StickyCell>
            <MetricRow metrics={row.metrics} />
          </tr>
        ))}
    </>
  )
}
